// useRoughNight — Phase D.1 §P10.9. A 12-hour "soften everything"
// switch for tough nights. While active, the Today screen dims
// celebratory surfaces (consistency pulse, money/hours saved cards),
// suppresses motivational toasts (via the existing honest-day path),
// and swaps the headline copy to a non-measuring tone.
//
// Support surfaces (Urge-surf, Breathe, Crisis link) stay visible
// and at full opacity — this hook never gates them.
//
// State lives in users.settings.roughNightUntil as an ISO timestamp.
// Activate stamps now+12h; deactivate clears it. Both go through the
// existing PUT /api/me/settings partial-merge endpoint.

import { useAuth } from '../auth/AuthContext';
import { apiPut } from '../lib/api';

export interface UseRoughNight {
  /** True while now < roughNightUntil. */
  active: boolean;
  /** Parsed end-time, or null when inactive / unset. */
  until: Date | null;
  /** Stamp roughNightUntil to now + 12h on the server. */
  activate: () => Promise<void>;
  /** Clear roughNightUntil on the server. */
  deactivate: () => Promise<void>;
}

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

export function useRoughNight(): UseRoughNight {
  const { me, refresh } = useAuth();
  const untilStr = me?.settings?.roughNightUntil ?? null;
  const until = untilStr ? new Date(untilStr) : null;
  // A bad Date.parse returns NaN — treat as inactive.
  const active =
    until !== null && !Number.isNaN(until.getTime()) && until.getTime() > Date.now();

  async function activate(): Promise<void> {
    const next = new Date(Date.now() + TWELVE_HOURS_MS).toISOString();
    await apiPut('/api/me/settings', { roughNightUntil: next });
    await refresh();
  }

  async function deactivate(): Promise<void> {
    await apiPut('/api/me/settings', { roughNightUntil: null });
    await refresh();
  }

  return { active, until: active ? until : null, activate, deactivate };
}
